import type { PlainDsdCell, PlainDsdTable } from "@/types/plain-dsd-table";
import type { DsdEditorParsedItem } from "@/utils/dsd-editor/document-items";

export type DsdEditorSumDifference = Record<string, number>;

export interface DsdEditorSumValidationChild {
  cellId: string;
  address: string;
  sectionName: string;
  value: string;
  sign: 1 | -1;
}

export interface DsdEditorSumValidationCell {
  cellId: string;
  address: string;
  sectionName: string;
  value: string;
  direction: string | null;
  isCaution: boolean;
  isParent: boolean;
  difference: DsdEditorSumDifference | null;
  children: DsdEditorSumValidationChild[];
  calculatedValue: string | null;
}

const SUM_DIRECTION_PRIORITY = ["up", "left", "down", "right"] as const;

export const getPlainDsdCellAddress = (cell: PlainDsdCell) =>
  cell.attrs["data-address"] ??
  cell.attrs["data-cell-address"] ??
  cell.attrs["data-dsd-address"] ??
  cell.attrs["data-dsd-editor-address"] ??
  cell.cellId;

export const isPlainDsdSumParentCell = (cell: PlainDsdCell) =>
  cell.attrs["data-sum"] === "true" || cell.attrs["data-sum-role"] === "parent";

export const isPlainDsdSumCautionCell = (cell: PlainDsdCell) =>
  cell.attrs["data-sum-caution"] === "true";

const parseSumChildren = (value: string | undefined) =>
  value
    ? value
        .split(",")
        .map((address) => address.trim())
        .filter(Boolean)
    : [];

const parseSumSigns = (value: string | undefined) =>
  value ? value.split(",").map((sign) => (sign.trim() === "-1" ? -1 : 1)) : [];

export const parseDsdEditorSumDifference = (
  value: string | undefined,
): DsdEditorSumDifference | null => {
  if (!value) return null;

  try {
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== "object") return null;

    const result: DsdEditorSumDifference = {};
    for (const [direction, amount] of Object.entries(parsed)) {
      if (typeof amount === "number" && Number.isFinite(amount)) {
        result[direction] = amount;
      }
    }

    return Object.keys(result).length > 0 ? result : null;
  } catch {
    return null;
  }
};

const getDsdEditorSumDifferenceValue = (
  difference: DsdEditorSumDifference | null,
) => {
  if (!difference) return null;

  for (const direction of SUM_DIRECTION_PRIORITY) {
    const value = difference[direction];
    if (value !== undefined && value !== 0) {
      return value;
    }
  }

  const fallback = Object.values(difference).find((value) => value !== 0);
  return fallback ?? null;
};

export const formatDsdEditorSumDifference = (
  difference: DsdEditorSumDifference | null,
) => {
  const value = getDsdEditorSumDifferenceValue(difference);
  if (value === null) return null;

  return (value > 0 ? "+" : "") + value.toLocaleString();
};

export const hasDsdEditorSumDifference = (
  difference: DsdEditorSumDifference | null,
) => !!difference && Object.values(difference).some((value) => value !== 0);

export const isDsdEditorSumMismatch = (cell: DsdEditorSumValidationCell) =>
  cell.isCaution || hasDsdEditorSumDifference(cell.difference);

export const getDsdEditorSumMismatchCells = (
  cells: DsdEditorSumValidationCell[],
) => cells.filter(isDsdEditorSumMismatch);

const parseFormattedNumber = (value: string) => {
  const trimmed = value.trim();
  const isParenNegative = trimmed.startsWith("(") && trimmed.endsWith(")");
  const normalized = (isParenNegative ? trimmed.slice(1, -1) : trimmed)
    .replace(/,/g, "")
    .replace(/[^\d.-]/g, "");
  if (!normalized || normalized === "-" || normalized === ".") return null;

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return null;

  return isParenNegative ? -parsed : parsed;
};

const formatAccountingNumber = (value: number) =>
  value < 0 ? `(${Math.abs(value).toLocaleString()})` : value.toLocaleString();

const calculateSumValue = (children: DsdEditorSumValidationChild[]) => {
  if (children.length === 0) return null;

  let hasParsedValue = false;
  const sum = children.reduce((total, child) => {
    const parsed = parseFormattedNumber(child.value);
    if (parsed === null) return total;

    hasParsedValue = true;
    return total + parsed * child.sign;
  }, 0);

  return hasParsedValue ? formatAccountingNumber(sum) : null;
};

const getSumChildCells = (
  table: PlainDsdTable,
  parentCell: PlainDsdCell,
  sectionName: string,
): DsdEditorSumValidationChild[] => {
  const explicitChildAddresses = parseSumChildren(
    parentCell.attrs["data-sum-children"],
  );
  const explicitSigns = parseSumSigns(parentCell.attrs["data-sum-signs"]);
  const parentAddress = getPlainDsdCellAddress(parentCell);
  const cellByAddress = new Map<string, PlainDsdCell>();
  const children: DsdEditorSumValidationChild[] = [];

  for (const row of table.rows) {
    for (const cell of row.cells) {
      if (cell.cellId === parentCell.cellId) continue;

      const cellAddress = getPlainDsdCellAddress(cell);
      cellByAddress.set(cellAddress, cell);
      cellByAddress.set(cell.cellId, cell);
    }
  }

  if (explicitChildAddresses.length > 0) {
    explicitChildAddresses.forEach((childAddress, childIndex) => {
      const cell = cellByAddress.get(childAddress);
      if (!cell) return;

      children.push({
        cellId: cell.cellId,
        address: getPlainDsdCellAddress(cell),
        sectionName,
        value: cell.value,
        sign: explicitSigns[childIndex] ?? 1,
      });
    });

    return children;
  }

  for (const row of table.rows) {
    for (const cell of row.cells) {
      if (cell.cellId === parentCell.cellId) continue;

      const cellAddress = getPlainDsdCellAddress(cell);
      const childParentAddress = cell.attrs["data-sum-parent"];

      if (!!parentAddress && childParentAddress === parentAddress) {
        children.push({
          cellId: cell.cellId,
          address: cellAddress,
          sectionName,
          value: cell.value,
          sign: 1,
        });
      }
    }
  }

  return children;
};

export const getDsdEditorSumValidationCells = (
  items: DsdEditorParsedItem[],
) => {
  const cells: DsdEditorSumValidationCell[] = [];

  for (const item of items) {
    if (item.kind !== "table") continue;

    for (const row of item.table.rows) {
      for (const cell of row.cells) {
        const isParent = isPlainDsdSumParentCell(cell);
        const isCaution = isPlainDsdSumCautionCell(cell);
        if (!isParent && !isCaution) continue;

        const children = isParent
          ? getSumChildCells(item.table, cell, item.sectionName)
          : [];
        const calculatedValue =
          calculateSumValue(children) ??
          cell.attrs["data-sum-calculated-value"] ??
          null;

        cells.push({
          cellId: cell.cellId,
          address: getPlainDsdCellAddress(cell),
          sectionName: item.sectionName,
          value: cell.value,
          direction: cell.attrs["data-sum-direction"] ?? null,
          isCaution,
          isParent,
          difference: parseDsdEditorSumDifference(
            cell.attrs["data-sum-difference"],
          ),
          children,
          calculatedValue,
        });
      }
    }
  }

  return cells;
};
